import { useState, useEffect, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { useApiConfig } from "common-fe";
import { useTranslation } from "i18n-fe";
import { OrganizationMemberApi, Configuration } from "account-common";
import { extractErrorMessage } from "../utils/errorUtils";

const ACCOUNT_API_BASE_URL = import.meta.env.VITE_ACCOUNT_API_BASE_URL;

// First-page size. ≤ this ⇒ the whole org is loaded and the UI searches
// client-side; a present nextCursor ⇒ a "large" org that searches server-side.
export const MEMBERS_PAGE_SIZE = 20;

export const useOrganizationMembers = (orgId) => {
  const auth = useAuth();
  const buildConfig = useApiConfig();
  const { t } = useTranslation("common");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Set up the API client
  const api = useCallback(() => {
    const accessToken = auth.user?.access_token;
    if (!accessToken) return null;
    const config = new Configuration(buildConfig(ACCOUNT_API_BASE_URL));
    return new OrganizationMemberApi(config);
  }, [auth.user?.access_token, buildConfig]);

  // Fetch members using the new listOrgMembers endpoint
  const fetchMembers = useCallback(async ({ silent = false } = {}) => {
    const memberApi = api();
    if (!memberApi || !orgId) {
      setLoading(false);
      return;
    }

    try {
      // A silent refetch keeps the page mounted (no skeleton early-return) so an
      // open dialog isn't torn down — used after in-dialog role changes.
      if (!silent) setLoading(true);
      setError(null);
      // Page size 20: the overwhelming majority of orgs fit in one page (so the
      // UI can search client-side); a present nextCursor signals a "large" org
      // (>20) that should search server-side. See MEMBERS_PAGE_SIZE.
      const response = await memberApi.listOrganizationMembers(orgId, MEMBERS_PAGE_SIZE);

      if (response.data.status === "success" && response.data.data) {
        setMembers(response.data.data.members || []);
        setNextCursor(response.data.data.nextCursor || null);
      } else {
        setError("Failed to load members");
      }
    } catch (err) {
      console.error("Failed to fetch members:", err);
      setError(extractErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  }, [api, orgId, t]);

  // Load the next page (if any) and append it. Bounds cost for huge orgs.
  const loadMore = useCallback(async () => {
    const memberApi = api();
    if (!memberApi || !orgId || !nextCursor) return;
    try {
      setLoadingMore(true);
      const response = await memberApi.listOrganizationMembers(
        orgId,
        MEMBERS_PAGE_SIZE,
        nextCursor
      );
      if (response.data.status === "success" && response.data.data) {
        setMembers((prev) => [...prev, ...(response.data.data.members || [])]);
        setNextCursor(response.data.data.nextCursor || null);
      }
    } catch (err) {
      console.error("Failed to load more members:", err);
      setError(extractErrorMessage(err, t));
    } finally {
      setLoadingMore(false);
    }
  }, [api, orgId, nextCursor, t]);

  // Find a single member by email (for large orgs). Returns the member, or null
  // if no such member in this org.
  const lookupByEmail = useCallback(
    async (email) => {
      const memberApi = api();
      if (!memberApi || !orgId) {
        throw new Error("No access token found or organization ID missing");
      }
      try {
        const response = await memberApi.lookupOrganizationMember(email, orgId);
        if (response.data.status === "success" && response.data.data) {
          return response.data.data.member;
        }
        return null;
      } catch (err) {
        if (err?.response?.status === 404) return null;
        throw new Error(extractErrorMessage(err, t));
      }
    },
    [api, orgId, t]
  );

  // Add member to organization using email
  const addMember = useCallback(async (email) => {
    const memberApi = api();
    if (!memberApi || !orgId) {
      throw new Error("No access token found or organization ID missing");
    }

    try {
      const response = await memberApi.createOrganizationMember(orgId, {
        email: email
      });
      
      if (response.data.status === "success" && response.data.data) {
        // Add the new member to the list
        const newMember = response.data.data.member;
        setMembers(prev => [...prev, newMember]);
        return response.data.data;
      } else {
        throw new Error("Failed to add member");
      }
    } catch (err) {
      console.error("Failed to add member:", err);
      throw new Error(extractErrorMessage(err, t));
    }
  }, [api, orgId, t]);

  // Remove member from organization
  const removeMember = useCallback(async (userId) => {
    const memberApi = api();
    if (!memberApi || !orgId) {
      throw new Error("No access token found or organization ID missing");
    }

    try {
      const response = await memberApi.removeOrganizationMember(orgId, userId);
      
      if (response.data.status === "success") {
        // Remove the member from the list
        setMembers(prev => prev.filter(member => member.userId !== userId));
        return response.data;
      } else {
        throw new Error("Failed to remove member");
      }
    } catch (err) {
      console.error("Failed to remove member:", err);
      throw new Error(extractErrorMessage(err, t));
    }
  }, [api, orgId, t]);

  // Update member permissions (owner status)
  const updateMemberPermissions = useCallback(async (userId, isOwner) => {
    const memberApi = api();
    if (!memberApi || !orgId) {
      throw new Error("No access token found or organization ID missing");
    }

    try {
      const response = await memberApi.editOrganizationMember(orgId, userId, {
        owner: isOwner
      });
      
      if (response.data.status === "success") {
        // Update the member in the list
        setMembers(prev => 
          prev.map(member => 
            member.userId === userId 
              ? { ...member, owner: isOwner }
              : member
          )
        );
        return response.data;
      } else {
        throw new Error("Failed to update member permissions");
      }
    } catch (err) {
      console.error("Failed to update member permissions:", err);
      throw new Error(extractErrorMessage(err, t));
    }
  }, [api, orgId, t]);

  // Fetch members on mount and when orgId changes
  useEffect(() => {
    if (orgId) {
      fetchMembers();
    }
  }, [fetchMembers, orgId]);

  return {
    members,
    loading,
    error,
    nextCursor,
    loadingMore,
    fetchMembers,
    loadMore,
    lookupByEmail,
    addMember,
    removeMember,
    updateMemberPermissions,
    refetch: fetchMembers,
    refetchSilent: () => fetchMembers({ silent: true })
  };
}; 